import TencentCloudChat from '@tencentcloud/chat';

const DB_NAME = 'spt-im-cache';
const DB_VERSION = 2;
const STORE_IM_CACHE = 'im-cache';
const CACHE_SWITCH_KEY = 'imCacheEnabled';
const CACHE_DEBUG_KEY = 'imCacheDebug';
const MSG_EXT_DATA_KEY = '__spt_ext_data__';

const MESSAGE_PAGE_SIZE = 20;
const MESSAGE_CACHE_LIMIT = 1000;
const DEFAULT_DEBOUNCE_MS = 500;
const USER_INFO_CACHE_LIMIT = 2000;
const USER_INFO_REFRESH_MIN_INTERVAL_MS = 10 * 60 * 1000;
const USER_INFO_REFRESH_BATCH_SIZE = 100;
const USER_INFO_LOW_PRIORITY_DEBOUNCE_MS = 300 * 1000;
const USER_INFO_HIGH_PRIORITY_DEBOUNCE_MS = 1000;
const GROUP_INFO_CACHE_TTL_MS = 2 * 60 * 1000;
const GROUP_MEMBER_CACHE_TTL_MS = 2 * 60 * 1000;
const USER_STATUS_BATCH_SIZE = 100;
const USER_STATUS_POLL_INTERVAL_MS = 30 * 1000;

let dbPromise = null;
const memoryRecords = new Map();
const flushTimerMap = new Map();

const deleteDb = async () => {
  if (!isIndexedDbAvailable()) {
    return false;
  }
  return new Promise((resolve) => {
    const req = window.indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve(true);
    req.onerror = () => resolve(false);
    req.onblocked = () => resolve(false);
  });
};

const isIndexedDbAvailable = () => typeof window !== 'undefined' && !!window.indexedDB;

const isDebugOn = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }
  return window.localStorage.getItem(CACHE_DEBUG_KEY) === 'true';
};

const debugLog = (...args) => {
  if (isDebugOn()) {
    console.log('[IM_CACHE_DEBUG]', ...args);
  }
};

const getCacheSwitch = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return true;
  }
  return window.localStorage.getItem(CACHE_SWITCH_KEY) !== 'false';
};

const safeClone = (value) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    debugLog('safeClone failed', error);
    return null;
  }
};

const openDb = () => {
  if (!isIndexedDbAvailable()) {
    return Promise.resolve(null);
  }
  if (dbPromise) {
    return dbPromise;
  }
  dbPromise = new Promise((resolve) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (db.objectStoreNames.contains('conversations') && !db.objectStoreNames.contains(STORE_IM_CACHE)) {
        const oldStore = event.target.transaction.objectStore('conversations');
        const newStore = db.createObjectStore(STORE_IM_CACHE, { keyPath: 'key' });
        oldStore.openCursor().onsuccess = (cursorEvent) => {
          const cursor = cursorEvent.target.result;
          if (cursor) {
            newStore.put(cursor.value);
            cursor.continue();
          }
        };
      } else if (!db.objectStoreNames.contains(STORE_IM_CACHE)) {
        db.createObjectStore(STORE_IM_CACHE, { keyPath: 'key' });
      }
      if (db.objectStoreNames.contains('conversations')) {
        db.deleteObjectStore('conversations');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      debugLog('openDb failed', request.error);
      resolve(null);
    };
  });
  return dbPromise;
};

const recoverDb = async (reason) => {
  debugLog('recoverDb start', reason);
  try {
    const db = await dbPromise;
    if (db && typeof db.close === 'function') {
      db.close();
    }
  } catch (error) {
    debugLog('recoverDb close old db failed', error);
  }
  dbPromise = null;
  await deleteDb();
  const db = await openDb();
  debugLog('recoverDb done', !!db);
  return db;
};

const withStore = async (mode, callback, retried = false) => {
  const db = await openDb();
  if (!db) {
    return null;
  }
  let tx = null;
  try {
    tx = db.transaction([STORE_IM_CACHE], mode);
  } catch (error) {
    debugLog('withStore failed', error);
    if (!retried && error?.name === 'NotFoundError') {
      await recoverDb('store missing');
      return withStore(mode, callback, true);
    }
    return null;
  }
  return new Promise((resolve) => {
    try {
      const store = tx.objectStore(STORE_IM_CACHE);
      callback(store, resolve);
    } catch (error) {
      debugLog('withStore failed after tx', error);
      resolve(null);
    }
  });
};

const readRecordFromDb = async (key) => {
  return withStore('readonly', (store, resolve) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => resolve(null);
  });
};

const writeRecordToDb = async (record) => {
  return withStore('readwrite', (store, resolve) => {
    const req = store.put(record);
    req.onsuccess = () => resolve(true);
    req.onerror = () => resolve(false);
  });
};

const deleteRecordFromDb = async (key) => {
  return withStore('readwrite', (store, resolve) => {
    const req = store.delete(key);
    req.onsuccess = () => resolve(true);
    req.onerror = () => resolve(false);
  });
};

const scanAndPatchRecords = async (prefix, patcher) => {
  return withStore('readwrite', (store, resolve) => {
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        resolve(true);
        return;
      }
      const value = cursor.value;
      if (value?.key && value.key.startsWith(prefix)) {
        const patched = patcher(safeClone(value));
        if (patched) {
          cursor.update(patched);
          memoryRecords.set(value.key, patched);
        }
      }
      cursor.continue();
    };
    req.onerror = () => resolve(false);
  });
};

const getRecord = async (key) => {
  if (!key || !getCacheSwitch()) {
    return null;
  }
  if (memoryRecords.has(key)) {
    return safeClone(memoryRecords.get(key));
  }
  const fromDb = await readRecordFromDb(key);
  if (fromDb) {
    memoryRecords.set(key, fromDb);
    return safeClone(fromDb);
  }
  return null;
};

const flushRecord = async (key) => {
  if (!memoryRecords.has(key) || !getCacheSwitch()) {
    return false;
  }
  const record = safeClone(memoryRecords.get(key));
  if (!record) {
    return false;
  }
  const ok = await writeRecordToDb(record);
  if (!ok) {
    debugLog('flushRecord failed', key);
  }
  return ok;
};

const setRecord = async (key, data, debounceMs = DEFAULT_DEBOUNCE_MS) => {
  if (!key || !getCacheSwitch()) {
    return false;
  }
  const record = {
    key,
    data: safeClone(data),
    updatedAt: Date.now(),
  };
  if (record.data == null) {
    return false;
  }
  memoryRecords.set(key, record);
  if (!debounceMs || debounceMs <= 0) {
    return flushRecord(key);
  }
  if (flushTimerMap.has(key)) {
    clearTimeout(flushTimerMap.get(key));
  }
  const timer = setTimeout(() => {
    flushTimerMap.delete(key);
    flushRecord(key).then(() => { }).catch(() => { });
  }, debounceMs);
  flushTimerMap.set(key, timer);
  return true;
};

const deleteRecord = async (key) => {
  if (!key) {
    return false;
  }
  if (flushTimerMap.has(key)) {
    clearTimeout(flushTimerMap.get(key));
    flushTimerMap.delete(key);
  }
  memoryRecords.delete(key);
  return deleteRecordFromDb(key);
};

const toConversationKey = (userId) => `${userId}:conversations`;
const toMessageKey = (userId, conversationID) => `${userId}:conversation:${conversationID}`;
const toUserInfoKey = (userId) => `${userId}:userinfos`;
const toGroupMembersKey = (userId, groupId, role) => `${userId}:groupMembers:${groupId}:${role || 'ALL'}`;
const toGroupInfoKey = (userId, groupId) => `${userId}:groupInfo:${groupId}`;
const toFriendApplicationKey = (userId) => `${userId}:friendApplication`;

const getMessageId = (message) => message?.ID || message?.id || message?.msgID || '';
const getMessageTime = (message) => Number(message?.time || message?.lastTime || message?.clientTime || 0);
const getMessageSeq = (message) => Number(message?.sequence || message?.lastSequence || message?.seq || message?.messageSeq || 0);

const dedupeMessages = (messages = []) => {
  // 相通ID的对象，只保留数组中第一个
  const seen = new Set();
  const result = [];
  messages.forEach((message) => {
    const id = getMessageId(message);
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    result.push(message);
  });
  return result;
};

const sortMessagesAsc = (messages = []) => {
  return [...messages].sort((a, b) => {
    const ta = getMessageTime(a);
    const tb = getMessageTime(b);
    if (ta !== tb) {
      return ta - tb;
    }
    const sa = getMessageSeq(a);
    const sb = getMessageSeq(b);
    if (sa !== sb) {
      return sa - sb;
    }
    return `${getMessageId(a)}`.localeCompare(`${getMessageId(b)}`);
  });
};

const appendMessageWithRules = (targetMessages, message, isGroup, adopt) => {
  const id = getMessageId(message);
  if (!id) {
    return false;
  }
  if (targetMessages.find((item) => getMessageId(item) === id)) {
    return false;
  }
  if (!targetMessages.length || adopt) {
    targetMessages.push(message);
    return true;
  }
  const last = targetMessages[targetMessages.length - 1];
  const canAppend = isGroup
    ? getMessageSeq(message) > getMessageSeq(last)
    : getMessageTime(message) > getMessageTime(last);
  if (!canAppend) {
    return false;
  }
  targetMessages.push(message);
  return true;
};

const updateExistMessage = (targetMessages, message) => {
  const id = getMessageId(message);
  if (!id) {
    return false;
  }
  const index = targetMessages.findIndex((item) => getMessageId(item) === id);
  if (index < 0) {
    return false;
  }
  // #warning 未确定扩展属性名称。确定后，这里要对扩展属性做判断再合并
  assignMessage(targetMessages[index], message);
  return true;
}

const assignMessage = (targetMessage, sourceMessage) => {
  return Object.assign(targetMessage, sourceMessage);
}

const mergeMessagesWithAssign = (baseMessages = [], incomingMessages = []) => {
  const merged = [];
  const upsert = (message) => {
    const id = getMessageId(message);
    if (!id) {
      return;
    }
    const index = merged.findIndex((item) => getMessageId(item) === id);
    if (index < 0) {
      merged.push(message);
      return;
    }
    assignMessage(merged[index], message);
  };

  baseMessages.forEach(upsert);
  incomingMessages.forEach(upsert);
  return sortMessagesAsc(merged);
}

const trimForLimit = (messages = [], limit = MESSAGE_CACHE_LIMIT) => {
  if (messages.length <= limit) {
    return {
      messages,
      trimmedCount: 0,
      // nextReqMessageID: null,
    };
  }
  const trimmedMessages = messages.slice(messages.length - limit);
  return {
    messages: trimmedMessages,
    trimmedCount: messages.length - trimmedMessages.length,
    // nextReqMessageID: trimmedMessages.length ? getMessageId(trimmedMessages[0]) : null,
  };
};

const getDefaultMessageState = () => ({
  coreMessages: [],
  coreBoundaryCompleted: false,
  detachedMessages: [],
  detachedBoundaryCompleted: false,
});

const readMessageState = async (userId, conversationID) => {
  const record = await getRecord(toMessageKey(userId, conversationID));
  if (!record || !record.data || typeof record.data !== 'object') {
    return getDefaultMessageState();
  }
  const state = record.data;
  return {
    coreMessages: Array.isArray(state.coreMessages) ? mergeMessagesWithAssign([], state.coreMessages) : [],
    coreBoundaryCompleted: !!state.coreBoundaryCompleted,
    detachedMessages: Array.isArray(state.detachedMessages) ? mergeMessagesWithAssign([], state.detachedMessages) : [],
    detachedBoundaryCompleted: !!state.detachedBoundaryCompleted,
  };
};

const writeMessageState = async (userId, conversationID, state, debounceMs = DEFAULT_DEBOUNCE_MS) => {
  const nextState = {
    coreMessages: trimForLimit(mergeMessagesWithAssign([], state.coreMessages || [])).messages,
    coreBoundaryCompleted: !!state.coreBoundaryCompleted,
    detachedMessages: trimForLimit(mergeMessagesWithAssign([], state.detachedMessages || [])).messages,
    detachedBoundaryCompleted: !!state.detachedBoundaryCompleted,
  };
  return setRecord(toMessageKey(userId, conversationID), nextState, debounceMs);
};

// 是否需要删除消息，比如系统消息，或者撤回消息等，不需要显示的信息
// 返回对象{deletable: true, payload: {}}，deletable表示是否需要删除，payload表示自定义消息其中的payload的反序列化对象 
const isDeletableMsg = (e, userId) => {
  if (e.type == TencentCloudChat.TYPES.MSG_GRP_TIP && e.payload.operationType == 7) {
    return { deletable: true, payload: null };//过滤掉群提示信息的类型7
  } else if (e.type == TencentCloudChat.TYPES.MSG_CUSTOM) {
    let _e = JSON.parse(JSON.stringify(e));
    if (typeof _e.payload.data == 'string') {
      try {
        _e.payload.data = JSON.parse(_e.payload.data);
      } catch (err) { }
    }
    if (_e.payload.data?.event == 'redPacketReceive' && _e.payload.data?.data.senderId != userId) {//不是自己的红包回执不要
      return { deletable: true, payload: _e.payload.data };
    } else {
      return { deletable: false, payload: null };
    }
  } else {
    return { deletable: false, payload: null };
  }
}

const isVisibleMsg = (e, role, userId) => {
  var { deletable, payload } = isDeletableMsg(e, userId);
  if (!deletable) {
    if (e.type == TencentCloudChat.TYPES.MSG_GRP_TIP) {
      if ([2, 3].some(s => s == e.payload.operationType) && !["Owner", "Admin"].some(e => e == role)) {
        //不是管理员不显示退群和踢人信息
        return false;
      }
    }
    if (e.type == TencentCloudChat.TYPES.MSG_CUSTOM) {
      if (payload && payload.event == 'memberRemoved' && !["Owner", "Admin"].some(e => e == role)) {//不是管理员不显示退群和踢人信息
        return false;
      }
    }
    return true;
  } else {
    return false;
  }
};

// 返回的消息列表不包含 cursorId对应的消息，且是cursorId之前的pageSize条消息
const getPageBeforeCursor = (messages = [], cursorId, pageSize = MESSAGE_PAGE_SIZE, role, userId) => {
  var index = messages.length;
  if (cursorId) {
    index = messages.findIndex((item) => getMessageId(item) === cursorId);
  }
  if (index <= 0) {
    return { isCompleted: true, page: [] };
  }
  const page = [];
  let isCompleted = false;
  for (let i = index - 1; i >= 0 && page.length < pageSize; i--) {
    isCompleted = i === 0;
    if (isVisibleMsg(messages[i], role, userId)) {
      page.unshift(messages[i]);
    } else {
      //不需要显示的信息，比如系统消息，或者撤回消息等，直接过滤掉，不计入pageSize限制，但仍然继续往前找，直到找到pageSize条需要显示的消息，或者没有消息了为止
    }
  }
  return { isCompleted, page };
};

const composeMessageListResponse = ({
  list,
  isCompleted
}) => {
  return {
    data: {
      messageList: list,
      nextReqMessageID: list.length ? getMessageId(list[0]) : null,
      isCompleted,
    },
  };
};

const resolveConversationLastMessageFingerprint = async (userId, conversationID) => {
  const record = await getRecord(toConversationKey(userId));
  const list = record?.data;
  if (!Array.isArray(list)) {
    return '';
  }
  const matched = list.find((item) => item?.conversationID === conversationID);
  if (!matched) {
    return {
      type: /^GROUP/.test(conversationID) ? TencentCloudChat.TYPES.CONV_GROUP : TencentCloudChat.TYPES.CONV_C2C,
      lastSeq: 0,
      lastTime: 0,
      lastMsg: null,
    };
  } else {
    return {
      type: matched?.type,
      lastSeq: matched?.lastMessage ? getMessageSeq(matched.lastMessage) : 0,
      lastTime: matched?.lastMessage ? getMessageTime(matched.lastMessage) : 0,
      lastMsg: matched?.lastMessage || null,
    };
  }
};

const isGroupConversation = (conversationID, message) => {
  if (message?.conversationType) {
    return message.conversationType === TencentCloudChat.TYPES.CONV_GROUP;
  }
  return `${conversationID}`.startsWith('GROUP');
};

export const getUserConversationsCache = async (userId, maxAgeMs = 45000, requireFresh = false) => {
  if (!userId || !getCacheSwitch()) {
    return null;
  }
  const record = await getRecord(toConversationKey(userId));
  if (!record || !Array.isArray(record.data)) {
    return null;
  }
  if (requireFresh && Date.now() - Number(record.updatedAt || 0) > maxAgeMs) {
    return null;
  }
  return record.data;
};

export const createImCacheManager = ({
  getChatClient,
  getUserId,
}) => {
  const getCurrentUserId = () => `${getUserId?.() || ''}`;
  const createStats = () => ({
    conversationList: { total: 0, hit: 0, miss: 0, bypass: 0, remote: 0 },
    messageList: { total: 0, hit: 0, miss: 0, bypass: 0, remote: 0, boundaryHit: 0 },
    userInfo: { total: 0, hit: 0, miss: 0, bypass: 0, remote: 0 },
    groupInfo: { total: 0, hit: 0, miss: 0, bypass: 0, remote: 0 },
    groupMembers: { total: 0, hit: 0, miss: 0, bypass: 0, remote: 0 },
    writes: { conversationPushUpdate: 0, messagePushUpdate: 0, detachedClear: 0 },
  });
  let stats = createStats();
  let verboseTrace = false;
  const pendingConversationMap = new Map();
  const pendingGroupInfoMap = new Map();
  const pendingGroupMembersMap = new Map();

  let userStatusOwner = '';
  const userStatusMap = new Map();
  const userStatusScopeMap = new Map();
  const userStatusPollTimerMap = new Map();

  let userInfoCacheOwner = '';
  const userInfoMemoryMap = new Map();
  const userInfoRefreshHighWaiting = new Set();
  const userInfoRefreshLowWaiting = new Set();
  const userInfoRefreshHighReady = new Set();
  const userInfoRefreshLowReady = new Set();
  let userInfoRefreshHighTimer = null;
  let userInfoRefreshLowTimer = null;
  let userInfoRefreshRunning = false;

  const messageListLockMap = new Map();

  const withMessageStateLock = async (userId, conversationID, task) => {
    if (!userId || !conversationID || typeof task !== 'function') {
      return task?.();
    }
    const key = `${userId}|${conversationID}`;
    const previous = messageListLockMap.get(key) || Promise.resolve();
    const current = previous
      .catch(() => { })
      .then(() => task());
    messageListLockMap.set(key, current);
    try {
      return await current;
    } finally {
      if (messageListLockMap.get(key) === current) {
        messageListLockMap.delete(key);
      }
    }
  };

  const isTraceOn = () => {
    if (verboseTrace || isDebugOn()) {
      return true;
    }
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }
    return window.localStorage.getItem('imCacheTrace') === 'true';
  };

  const trace = (...args) => {
    if (isTraceOn()) {
      console.log('[IM_CACHE_TRACE]', ...args);
    }
  };

  const inc = (scope, key) => {
    if (!stats[scope]) {
      return;
    }
    stats[scope][key] = Number(stats[scope][key] || 0) + 1;
  };

  const normalizeUserStatusUserIds = (userIDList = [], excludeSelf = true) => {
    const selfUserId = getCurrentUserId();
    const seen = new Set();
    const result = [];
    (Array.isArray(userIDList) ? userIDList : []).forEach((raw) => {
      const userId = `${raw || ''}`.trim();
      if (!userId) {
        return;
      }
      if (excludeSelf && userId === selfUserId) {
        return;
      }
      if (seen.has(userId)) {
        return;
      }
      seen.add(userId);
      result.push(userId);
    });
    return result;
  };

  const splitUserStatusUserIds = (userIDList = [], chunkSize = USER_STATUS_BATCH_SIZE) => {
    if (!Array.isArray(userIDList) || !userIDList.length) {
      return [];
    }
    const chunks = [];
    for (let index = 0; index < userIDList.length; index += chunkSize) {
      chunks.push(userIDList.slice(index, index + chunkSize));
    }
    return chunks;
  };

  const mergeUserStatusList = (userStatusList = []) => {
    (Array.isArray(userStatusList) ? userStatusList : []).forEach((item) => {
      const userId = `${item?.userID || item?.userId || ''}`.trim();
      if (!userId) {
        return;
      }
      userStatusMap.set(userId, item);
    });
  };

  const clearUserStatusRuntime = () => {
    userStatusPollTimerMap.forEach((timer) => {
      if (typeof timer?.cancel === 'function') {
        timer.cancel();
      } else {
        clearInterval(timer);
      }
    });
    userStatusPollTimerMap.clear();
    userStatusScopeMap.clear();
    userStatusMap.clear();
    userStatusOwner = '';
  };

  const ensureUserStatusOwnerReady = () => {
    const owner = getCurrentUserId();
    if (!owner) {
      clearUserStatusRuntime();
      return '';
    }
    if (userStatusOwner && userStatusOwner !== owner) {
      clearUserStatusRuntime();
    }
    userStatusOwner = owner;
    return owner;
  };

  const ensureUserStatusSubscriptions = async (userIDList = []) => {
    const normalized = normalizeUserStatusUserIds(userIDList, true);
    if (!normalized.length) {
      return;
    }
    try {
      const chatClient = await getChatClient();
      if (typeof chatClient?.subscribeUserStatus !== 'function') {
        return;
      }
      for (const chunk of splitUserStatusUserIds(normalized)) {
        await chatClient.subscribeUserStatus({ userIDList: chunk });
      }
    } catch (error) {
      trace('subscribeUserStatus failed', `${error}`);
    }
  };

  const refreshUserStatus = async (userIDList = [], { subscribe = true } = {}) => {
    const owner = ensureUserStatusOwnerReady();
    const normalized = normalizeUserStatusUserIds(userIDList, true);
    if (!owner || !normalized.length) {
      return [];
    }
    const chatClient = await getChatClient();
    if (subscribe) {
      await ensureUserStatusSubscriptions(normalized);
    }

    const resultList = [];
    if (typeof chatClient?.getUserStatus !== 'function') {
      return resultList;
    }

    for (const chunk of splitUserStatusUserIds(normalized)) {
      try {
        const result = await chatClient.getUserStatus({ userIDList: chunk });
        const statuses = Array.isArray(result?.data) ? result.data : (Array.isArray(result?.data?.userStatusList) ? result.data.userStatusList : []);
        if (statuses.length) {
          resultList.push(...statuses);
          mergeUserStatusList(statuses);
        }
      } catch (error) {
        trace('getUserStatus failed', { chunk, error: `${error}` });
      }
    }

    return resultList;
  };

  const getCachedUserStatus = (userId) => {
    const key = `${userId || ''}`.trim();
    if (!key) {
      return null;
    }
    return userStatusMap.get(key) || null;
  };

  const getBatchCachedUserStatus = (userIDList = []) => {
    const result = {};
    normalizeUserStatusUserIds(userIDList, false).forEach((userId) => {
      const status = userStatusMap.get(userId);
      if (status) {
        result[userId] = status;
      }
    });
    return result;
  };

  const isUserOnline = (userId) => {
    const status = getCachedUserStatus(userId);
    const statusType = status?.statusType || status?.status || status?.state || '';
    return statusType === 'V2TIM_USER_STATUS_ONLINE' || statusType === 'ONLINE';
  };

  const stopUserStatusScopePolling = (scopeId) => {
    const timer = userStatusPollTimerMap.get(scopeId);
    if (timer) {
      if (typeof timer?.cancel === 'function') {
        timer.cancel();
      } else {
        clearInterval(timer);
      }
    }
    userStatusPollTimerMap.delete(scopeId);
  };

  const restartUserStatusScopePolling = (scopeId) => {
    stopUserStatusScopePolling(scopeId);
    const userIdSet = userStatusScopeMap.get(scopeId);
    if (!userIdSet || !userIdSet.size) {
      return;
    }
    const timer = setInterval(async () => {
      const current = Array.from(userStatusScopeMap.get(scopeId) || []);
      if (!current.length) {
        stopUserStatusScopePolling(scopeId);
        return;
      }
      await refreshUserStatus(current, { subscribe: true });
    }, USER_STATUS_POLL_INTERVAL_MS);
    userStatusPollTimerMap.set(scopeId, timer);
  };

  const registerUserStatusScope = async ({
    scopeId,
    userIDList,
    refreshNow = true,
    subscribe = true,
    enablePolling = true,
  } = {}) => {
    const owner = ensureUserStatusOwnerReady();
    if (!owner || !scopeId) {
      return;
    }
    const normalized = normalizeUserStatusUserIds(userIDList, true);
    if (!normalized.length) {
      await unregisterUserStatusScope(scopeId);
      return;
    }

    userStatusScopeMap.set(scopeId, new Set(normalized));
    if (refreshNow) {
      await refreshUserStatus(normalized, { subscribe });
    } else if (subscribe) {
      await ensureUserStatusSubscriptions(normalized);
    }

    if (enablePolling) {
      restartUserStatusScopePolling(scopeId);
    } else {
      stopUserStatusScopePolling(scopeId);
    }
  };

  const unregisterUserStatusScope = async (scopeId) => {
    if (!scopeId) {
      return;
    }
    stopUserStatusScopePolling(scopeId);
    userStatusScopeMap.delete(scopeId);
  };

  const resetUserInfoRuntime = () => {
    if (userInfoRefreshHighTimer) {
      clearTimeout(userInfoRefreshHighTimer);
    }
    if (userInfoRefreshLowTimer) {
      clearTimeout(userInfoRefreshLowTimer);
    }
    userInfoRefreshHighTimer = null;
    userInfoRefreshLowTimer = null;
    userInfoRefreshHighWaiting.clear();
    userInfoRefreshLowWaiting.clear();
    userInfoRefreshHighReady.clear();
    userInfoRefreshLowReady.clear();
    userInfoMemoryMap.clear();
    userInfoCacheOwner = '';
  };

  const trimUserInfoCache = () => {
    const overflow = userInfoMemoryMap.size - USER_INFO_CACHE_LIMIT;
    if (overflow <= 0) {
      return;
    }
    const entries = Array.from(userInfoMemoryMap.entries()).sort((a, b) => {
      const at = Number(a?.[1]?.touchedAt || 0);
      const bt = Number(b?.[1]?.touchedAt || 0);
      if (at !== bt) {
        return at - bt;
      }
      const ar = Number(a?.[1]?.refreshedAt || 0);
      const br = Number(b?.[1]?.refreshedAt || 0);
      return ar - br;
    });
    entries.slice(0, overflow).forEach(([id]) => {
      userInfoMemoryMap.delete(id);
      userInfoRefreshHighWaiting.delete(id);
      userInfoRefreshLowWaiting.delete(id);
      userInfoRefreshHighReady.delete(id);
      userInfoRefreshLowReady.delete(id);
    });
  };

  const saveUserInfoCache = async (owner) => {
    const items = {};
    userInfoMemoryMap.forEach((entry, id) => {
      items[id] = {
        userInfo: safeClone(entry?.userInfo || null),
        refreshedAt: Number(entry?.refreshedAt || 0),
        touchedAt: Number(entry?.touchedAt || 0),
      };
    });
    await setRecord(toUserInfoKey(owner), { items }, DEFAULT_DEBOUNCE_MS);
  };

  const ensureUserInfoCacheReady = async (owner) => {
    if (!owner) {
      resetUserInfoRuntime();
      return;
    }
    if (userInfoCacheOwner === owner) {
      return;
    }
    resetUserInfoRuntime();
    userInfoCacheOwner = owner;
    const record = await getRecord(toUserInfoKey(owner));
    const items = record?.data?.items;
    if (!items || typeof items !== 'object') {
      return;
    }
    Object.keys(items).forEach((id) => {
      const entry = items[id];
      if (!id || !entry || typeof entry !== 'object') {
        return;
      }
      userInfoMemoryMap.set(id, {
        userInfo: safeClone(entry.userInfo || null),
        refreshedAt: Number(entry.refreshedAt || 0),
        touchedAt: Number(entry.touchedAt || 0),
      });
    });
    trimUserInfoCache();
  };

  const markUserInfoRefreshed = async (userIDs, refreshedAt) => {
    userIDs.forEach((id) => {
      if (!id) {
        return;
      }
      const old = userInfoMemoryMap.get(id);
      userInfoMemoryMap.set(id, {
        userInfo: old?.userInfo || null,
        refreshedAt,
        touchedAt: refreshedAt,
      });
    });
    trimUserInfoCache();
    const owner = getCurrentUserId();
    if (owner) {
      await saveUserInfoCache(owner);
    }
  };

  const triggerUserInfoRefresh = () => {
    if (userInfoRefreshRunning) {
      return;
    }
    userInfoRefreshRunning = true;
    processUserInfoRefreshQueue().catch((error) => {
      trace('userInfo refresh queue failed', error);
    });
  };

  const processUserInfoRefreshQueue = async () => {
    try {
      while (true) {
        const owner = getCurrentUserId();
        if (!owner) {
          resetUserInfoRuntime();
          break;
        }
        await ensureUserInfoCacheReady(owner);

        let pending = [];
        if (userInfoRefreshHighReady.size) {
          pending = Array.from(userInfoRefreshHighReady);
          userInfoRefreshHighReady.clear();
        } else if (userInfoRefreshLowReady.size) {
          pending = Array.from(userInfoRefreshLowReady);
          userInfoRefreshLowReady.clear();
        } else {
          break;
        }

        const now = Date.now();
        const throttled = pending.filter((id) => {
          if (!id) {
            return false;
          }
          const last = Number(userInfoMemoryMap.get(id)?.refreshedAt || 0);
          return now - last >= USER_INFO_REFRESH_MIN_INTERVAL_MS;
        });
        if (!throttled.length) {
          continue;
        }

        await markUserInfoRefreshed(throttled, now);
        const chatClient = await getChatClient();
        const fetched = [];
        for (let start = 0; start < throttled.length; start += USER_INFO_REFRESH_BATCH_SIZE) {
          const slice = throttled.slice(start, start + USER_INFO_REFRESH_BATCH_SIZE);
          try {
            const res = await chatClient.getUserProfile({ userIDList: slice });
            const list = Array.isArray(res?.data) ? res.data : [];
            if (list.length) {
              fetched.push(...list);
            }
          } catch (error) {
            trace('refresh userInfo by queue failed', { slice, error: `${error}` });
          }
        }

        if (fetched.length) {
          const refreshedAt = Date.now();
          fetched.forEach((info) => {
            const id = `${info?.userID || ''}`;
            if (!id) {
              return;
            }
            userInfoMemoryMap.set(id, {
              userInfo: safeClone(info),
              refreshedAt,
              touchedAt: refreshedAt,
            });
          });
          trimUserInfoCache();
          await saveUserInfoCache(owner);
        }
      }
    } finally {
      userInfoRefreshRunning = false;
      if (userInfoRefreshHighReady.size || userInfoRefreshLowReady.size) {
        triggerUserInfoRefresh();
      }
    }
  };

  const scheduleUserInfoRefresh = (userID, highPriority) => {
    if (!userID) {
      return;
    }
    if (highPriority) {
      userInfoRefreshLowWaiting.delete(userID);
      userInfoRefreshLowReady.delete(userID);
      userInfoRefreshHighWaiting.add(userID);
      if (userInfoRefreshHighTimer) {
        clearTimeout(userInfoRefreshHighTimer);
      }
      userInfoRefreshHighTimer = setTimeout(() => {
        userInfoRefreshHighTimer = null;
        userInfoRefreshHighWaiting.forEach((id) => userInfoRefreshHighReady.add(id));
        userInfoRefreshHighWaiting.clear();
        triggerUserInfoRefresh();
      }, USER_INFO_HIGH_PRIORITY_DEBOUNCE_MS);
      return;
    }

    if (userInfoRefreshHighWaiting.has(userID) || userInfoRefreshHighReady.has(userID)) {
      return;
    }
    userInfoRefreshLowWaiting.add(userID);
    if (userInfoRefreshLowTimer) {
      clearTimeout(userInfoRefreshLowTimer);
    }
    userInfoRefreshLowTimer = setTimeout(() => {
      userInfoRefreshLowTimer = null;
      userInfoRefreshLowWaiting.forEach((id) => userInfoRefreshLowReady.add(id));
      userInfoRefreshLowWaiting.clear();
      triggerUserInfoRefresh();
    }, USER_INFO_LOW_PRIORITY_DEBOUNCE_MS);
  };

  const reqUserInfoInCache = async (userID) => {
    inc('userInfo', 'total');
    const owner = getCurrentUserId();
    if (!userID || !owner || !getCacheSwitch()) {
      inc('userInfo', 'bypass');
      return null;
    }
    await ensureUserInfoCacheReady(owner);
    const now = Date.now();
    const entry = userInfoMemoryMap.get(userID);
    if (entry) {
      entry.touchedAt = now;
      scheduleUserInfoRefresh(userID, false);
      trimUserInfoCache();
      await saveUserInfoCache(owner);
      inc('userInfo', 'hit');
      return safeClone(entry.userInfo);
    }
    scheduleUserInfoRefresh(userID, true);
    inc('userInfo', 'miss');
    return null;
  };

  const saveUsersInfoToCache = async (userInfoList = []) => {
    const owner = getCurrentUserId();
    if (!owner || !Array.isArray(userInfoList) || !userInfoList.length || !getCacheSwitch()) {
      return;
    }
    await ensureUserInfoCacheReady(owner);
    const now = Date.now();
    userInfoList.forEach((info) => {
      const id = `${info?.userID || ''}`;
      if (!id) {
        return;
      }
      userInfoMemoryMap.set(id, {
        userInfo: safeClone(info),
        refreshedAt: now,
        touchedAt: now,
      });
    });
    trimUserInfoCache();
    await saveUserInfoCache(owner);
  };

  const getUsersProfile = async ({ userIDList = [], forceRefresh = false } = {}) => {
    inc('userInfo', 'total');
    const owner = getCurrentUserId();
    const chatClient = await getChatClient();
    const ids = Array.isArray(userIDList) ? userIDList.map((id) => `${id || ''}`).filter(Boolean) : [];
    if (!ids.length || !owner || !getCacheSwitch()) {
      inc('userInfo', 'bypass');
      inc('userInfo', 'remote');
      return chatClient.getUserProfile({ userIDList: ids });
    }

    await ensureUserInfoCacheReady(owner);
    const now = Date.now();
    const resultMap = new Map();
    const missing = [];
    ids.forEach((id) => {
      const entry = userInfoMemoryMap.get(id);
      if (!forceRefresh && entry?.userInfo) {
        entry.touchedAt = now;
        resultMap.set(id, safeClone(entry.userInfo));
      } else {
        missing.push(id);
      }
    });

    if (missing.length) {
      inc('userInfo', 'remote');
      const remote = await chatClient.getUserProfile({ userIDList: missing });
      const remoteList = Array.isArray(remote?.data) ? remote.data : [];
      if (remoteList.length) {
        await saveUsersInfoToCache(remoteList);
        remoteList.forEach((item) => {
          const id = `${item?.userID || ''}`;
          if (id) {
            resultMap.set(id, safeClone(item));
          }
        });
      }
    }

    if (!missing.length) {
      inc('userInfo', 'hit');
    } else {
      inc('userInfo', 'miss');
    }
    trimUserInfoCache();
    await saveUserInfoCache(owner);
    return {
      data: ids.map((id) => resultMap.get(id)).filter(Boolean),
    };
  };

  const updateFriendApplicationList = async (list) => {
    const userId = getCurrentUserId();
    const cacheKey = toFriendApplicationKey(userId);
    const cached = await getRecord(cacheKey);
    const cachedData = cached?.data || [];
    if (cachedData) {
      list.forEach(e => {
        let has = cachedData.find(c => c.userID == e.userID);
        if (has) {
          Object.assign(has, e);
        } else {
          cachedData.push(e);
        }
      })
    }
    console.log('updateFriendApplicationList-cachedData', cachedData);
    await setRecord(toFriendApplicationKey(userId), cachedData, DEFAULT_DEBOUNCE_MS);
  };

  const getFriendApplicationList = async () => {
    const userId = getCurrentUserId();
    const cacheKey = toFriendApplicationKey(userId);
    const cached = await getRecord(cacheKey);
    const cachedData = cached?.data || [];
    return cachedData;
  }

  const getGroupProfile = async ({ groupID, forceRefresh = false } = {}) => {
    inc('groupInfo', 'total');
    const userId = getCurrentUserId();
    const chatClient = await getChatClient();
    if (!groupID || !userId || !getCacheSwitch()) {
      inc('groupInfo', 'bypass');
      inc('groupInfo', 'remote');
      return chatClient.getGroupProfile({ groupID });
    }

    const cacheKey = toGroupInfoKey(userId, groupID);
    if (!forceRefresh) {
      const cached = await getRecord(cacheKey);
      const updatedAt = Number(cached?.updatedAt || 0);
      if (cached?.data && Date.now() - updatedAt <= GROUP_INFO_CACHE_TTL_MS) {
        inc('groupInfo', 'hit');
        return { data: { group: safeClone(cached.data) } };
      }
    }

    if (pendingGroupInfoMap.has(cacheKey)) {
      inc('groupInfo', 'miss');
      return pendingGroupInfoMap.get(cacheKey);
    }

    inc('groupInfo', 'miss');
    inc('groupInfo', 'remote');
    const pending = (async () => {
      const remote = await chatClient.getGroupProfile({ groupID });
      const group = remote?.data?.group;
      if (group) {
        await setRecord(cacheKey, group, DEFAULT_DEBOUNCE_MS);
      }
      return remote;
    })();
    pendingGroupInfoMap.set(cacheKey, pending);
    try {
      return await pending;
    } finally {
      pendingGroupInfoMap.delete(cacheKey);
    }
  };

  const getGroupMemberList = async ({ groupID, role, count = 100, offset = 0, forceRefresh = false } = {}) => {
    inc('groupMembers', 'total');
    const userId = getCurrentUserId();
    const chatClient = await getChatClient();
    if (!groupID || !userId || !getCacheSwitch()) {
      inc('groupMembers', 'bypass');
      inc('groupMembers', 'remote');
      return chatClient.getGroupMemberList({ groupID, role, count, offset });
    }

    const cacheKey = toGroupMembersKey(userId, groupID, role);
    const canReadCache = !forceRefresh && Number(offset || 0) === 0;
    if (canReadCache) {
      const cached = await getRecord(cacheKey);
      const updatedAt = Number(cached?.updatedAt || 0);
      const cachedData = cached?.data;
      if (cachedData && Date.now() - updatedAt <= GROUP_MEMBER_CACHE_TTL_MS) {
        const cachedList = Array.isArray(cachedData.memberList) ? cachedData.memberList : [];
        inc('groupMembers', 'hit');
        return {
          data: {
            memberList: safeClone(cachedList),
            offset: Number(cachedData.offset || 0),
          },
        };
      }
    }

    const pendingKey = `${cacheKey}:${count}:${offset}`;
    if (pendingGroupMembersMap.has(pendingKey)) {
      inc('groupMembers', 'miss');
      return pendingGroupMembersMap.get(pendingKey);
    }

    inc('groupMembers', 'miss');
    inc('groupMembers', 'remote');
    const pending = (async () => {
      const remote = await chatClient.getGroupMemberList({ groupID, role, count, offset });
      const remoteData = remote?.data;
      if (Number(offset || 0) === 0 && remoteData && Array.isArray(remoteData.memberList)) {
        await setRecord(cacheKey, {
          memberList: remoteData.memberList,
          offset: Number(remoteData.offset || 0),
        }, DEFAULT_DEBOUNCE_MS);
      }
      return remote;
    })();
    pendingGroupMembersMap.set(pendingKey, pending);
    try {
      return await pending;
    } finally {
      pendingGroupMembersMap.delete(pendingKey);
    }
  };

  const getConversationList = async ({ forceRemote = false } = {}) => {
    inc('conversationList', 'total');
    const userId = getCurrentUserId();
    const chatClient = await getChatClient();
    if (!userId || !getCacheSwitch()) {
      inc('conversationList', 'bypass');
      inc('conversationList', 'remote');
      trace('MISS conversationList -> SDK.getConversationList (reason: bypass cache)', { userId, cacheEnabled: getCacheSwitch() });
      return chatClient.getConversationList();
    }
    if (!forceRemote) {
      const cached = await getRecord(toConversationKey(userId));
      if (Array.isArray(cached?.data)) {
        inc('conversationList', 'hit');
        trace('HIT conversationList', { userId, count: cached.data.length });
        return { data: { conversationList: cached.data } };
      }
    }
    if (pendingConversationMap.has(userId)) {
      inc('conversationList', 'miss');
      trace('MISS conversationList -> reuse pending SDK.getConversationList', { userId });
      return pendingConversationMap.get(userId);
    }
    inc('conversationList', 'miss');
    inc('conversationList', 'remote');
    trace('MISS conversationList -> SDK.getConversationList', { userId, forceRemote });
    const pending = (async () => {
      const remote = await chatClient.getConversationList();
      const remoteList = remote?.data?.conversationList || [];
      if (Array.isArray(remoteList)) {
        await setRecord(toConversationKey(userId), remoteList, DEFAULT_DEBOUNCE_MS);
      }
      return remote;
    })();
    pendingConversationMap.set(userId, pending);
    try {
      return await pending;
    } finally {
      pendingConversationMap.delete(userId);
    }
  };

  const handleConversationListUpdate = async (conversationList = []) => {
    const userId = getCurrentUserId();
    if (!userId || !Array.isArray(conversationList)) {
      return;
    }
    inc('writes', 'conversationPushUpdate');
    await setRecord(toConversationKey(userId), conversationList, DEFAULT_DEBOUNCE_MS);
  };

  const onLoginSuccess = async () => {
    ensureUserStatusOwnerReady();
    const owner = getCurrentUserId();
    if (!owner) {
      resetUserInfoRuntime();
      return;
    }
    await ensureUserInfoCacheReady(owner);
    // try {
    //   await getConversationList({ forceRemote: true });
    // } catch (error) {
    //   trace('onLoginSuccess force refresh conversationList failed', `${error}`);
    // }
    // 登录时不需要清理游离缓存。获取消息时再判断和执行
    // const userId = getCurrentUserId();
    // if (!userId) {
    //   return;
    // }
    // const prefix = `${userId}:conversation:`;
    // await scanAndPatchRecords(prefix, (record) => {
    //   if (!record?.data || typeof record.data !== 'object') {
    //     return null;
    //   }
    //   record.data.detachedMessages = [];
    //   record.updatedAt = Date.now();
    //   return record;
    // });
    // inc('writes', 'loginDetachedClear');
  };

  // extMap 格式：{ [messageId]: { a: 1, b: 2 } }，会和原有扩展对象合并
  // 设置缓存中的消息的扩展信息，并同步落库
  const writeMsgsExt = async (conversationID, extMap = {}) => {
    const userId = getCurrentUserId();
    if (!userId || !conversationID || !extMap || typeof extMap !== 'object' || Array.isArray(extMap) || !Object.keys(extMap).length || !getCacheSwitch()) {
      return { updatedIds: [], missedIds: [] };
    }
    const patchMap = new Map();
    Object.keys(extMap).forEach((rawId) => {
      const id = `${rawId || ''}`;
      if (!id) {
        return;
      }
      const payload = extMap[id];
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return;
      }
      const extPayload = safeClone(payload);
      if (!extPayload || typeof extPayload !== 'object') {
        return;
      }
      patchMap.set(id, extPayload);
    });
    if (!patchMap.size) {
      return { updatedIds: [], missedIds: [] };
    }

    return withMessageStateLock(userId, conversationID, async () => {
      const state = await readMessageState(userId, conversationID);
      const updatedIds = new Set();
      const applyPatch = (targetMessages = []) => {
        targetMessages.forEach((message) => {
          const id = getMessageId(message);
          if (!id || !patchMap.has(id)) {
            return;
          }
          const patch = patchMap.get(id) || {};
          const prevExt = message && typeof message === 'object' ? message[MSG_EXT_DATA_KEY] : null;
          const mergedExt = Object.assign({}, (prevExt && typeof prevExt === 'object') ? prevExt : {}, patch);
          assignMessage(message, { [MSG_EXT_DATA_KEY]: mergedExt });
          updatedIds.add(id);
        });
      };

      applyPatch(state.coreMessages);
      applyPatch(state.detachedMessages);

      if (updatedIds.size) {
        await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
      }

      const missedIds = Array.from(patchMap.keys()).filter((id) => !updatedIds.has(id));
      return {
        updatedIds: Array.from(updatedIds),
        missedIds,
      };
    });
  };

  // 读取缓存中消息的扩展信息的副本
  const readMsgsExt = async (conversationID, messageIDs = []) => {
    const userId = getCurrentUserId();
    if (!userId || !conversationID || !getCacheSwitch()) {
      return {};
    }
    if (!Array.isArray(messageIDs) || !messageIDs.length) {
      return {};
    }

    const wanted = new Set(messageIDs.map((id) => `${id || ''}`).filter(Boolean));
    if (!wanted.size) {
      return {};
    }

    const extMap = {};
    const state = await readMessageState(userId, conversationID);
    const allMessages = (state.coreMessages || []).concat(state.detachedMessages || []);
    allMessages.forEach((message) => {
      const id = getMessageId(message);
      if (!id || !wanted.has(id)) {
        return;
      }
      const extData = message && typeof message === 'object' ? message[MSG_EXT_DATA_KEY] : null;
      if (extData && typeof extData === 'object') {
        extMap[id] = safeClone(extData);
      }
    });
    return extMap;
  };

  // 获取 message 的扩展数据。返回值是一个对象，可能包含任意字段，取决于之前写入的内容
  // 特性1：如果原message没有扩展数据，则返回null
  // 特性2：返回消息对象的“引用”——外部修改可引发message的变动！
  const getMsgExt = (message) => {
    if (!message || typeof message !== 'object') {
      return null;
    }
    const extData = message[MSG_EXT_DATA_KEY];
    if (extData && typeof extData === 'object') {
      return extData;
    }
    return null;
  };

  // 设置 message 的扩展数据。ext 是一个对象，可能包含任意字段，取决于业务需求
  // 特性1：如果 message 参数无效，或者 ext 参数无效，则不执行任何操作，返回 null
  // 特性2：如果 message 原来没有扩展数据，则创建一个新的扩展对象并与 message 关联；
  // 特性3：返回 message 的扩展对象的“引用”——外部修改可引发message的变动！
  const setMsgExt = (message, ext = {}) => {
    if (!message || typeof message !== 'object' || !ext || typeof ext !== 'object' || Array.isArray(ext)) {
      return null;
    }
    const prevExt = message[MSG_EXT_DATA_KEY];
    if (prevExt && typeof prevExt === 'object' && !Array.isArray(prevExt)) {
      Object.assign(prevExt, ext);
      return prevExt;
    }
    // const nextExt = Object.assign({}, ext);
    assignMessage(message, { [MSG_EXT_DATA_KEY]: ext });
    return message[MSG_EXT_DATA_KEY];
  };

  const handleRedPacketReceived = async (messages = []) => {
    const userId = getCurrentUserId();
    if (!userId || !Array.isArray(messages) || !messages.length || !getCacheSwitch()) {
      return;
    }
    const grouped = new Map();
    messages.forEach((message) => {
      const conversationID = message?.conversationID;
      console.log('updateRedPacket3-conversationID', conversationID);
      if (!conversationID) {
        return;
      }
      if (!grouped.has(conversationID)) {
        grouped.set(conversationID, []);
      }
      grouped.get(conversationID).push(message);

    });
    for (const [conversationID, list] of grouped.entries()) {
      await withMessageStateLock(userId, conversationID, async () => {
        const state = await readMessageState(userId, conversationID);
        for (const message of list) {
          updateExistMessage(state.coreMessages, message);
        }
        const sortedIncoming = mergeMessagesWithAssign([], list);
        sortedIncoming.forEach((message) => {
          appendMessageWithRules(state.coreMessages, message, false, true);
        });
        console.log('handleRedPacketReceived', state);
        await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
      });
    }
  }
  const getRedPacketReceived = async (conversationID, { userId: _userId } = {}) => {
    let userId = getCurrentUserId();
    if (_userId && !userId) {
      userId = _userId
    }
    const state = await readMessageState(userId, conversationID);
    console.log('getRedPacketReceived', state);
    return state.coreMessages;
  }

  const handleMessageReceived = async (messages = []) => {
    const userId = getCurrentUserId();
    if (!userId || !Array.isArray(messages) || !messages.length || !getCacheSwitch()) {
      return;
    }
    const grouped = new Map();
    messages.forEach((message) => {
      const conversationID = message?.conversationID;
      if (!conversationID) {
        return;
      }
      if (!grouped.has(conversationID)) {
        grouped.set(conversationID, []);
      }
      grouped.get(conversationID).push(message);

    });

    for (const [conversationID, list] of grouped.entries()) {
      await withMessageStateLock(userId, conversationID, async () => {
        const state = await readMessageState(userId, conversationID);

        for (const message of list) {
          //依赖ID，先更新一次。这样可以确保time和seq对上
          updateExistMessage(state.coreMessages, message);
          updateExistMessage(state.detachedMessages, message);
        }

        let isContinuous = false;
        const conversationLastMessageFingerprint = await resolveConversationLastMessageFingerprint(userId, conversationID);
        if (!state.coreMessages.length) isContinuous = false;
        else {
          if (list.some(msg => getMessageSeq(msg) >= conversationLastMessageFingerprint.lastSeq || getMessageTime(msg) >= conversationLastMessageFingerprint.lastTime)) {
            console.log('新消息连续', { conversationID, conversationLastMessageFingerprint, incomingSeqs: list.map(getMessageSeq), incomingTimes: list.map(getMessageTime) });
            isContinuous = true;
          } else {
            if (conversationLastMessageFingerprint.type == TencentCloudChat.TYPES.CONV_GROUP) {
              isContinuous = getMessageSeq(state.coreMessages[state.coreMessages.length - 1]) >= conversationLastMessageFingerprint.lastSeq;
            } else {
              isContinuous = getMessageTime(state.coreMessages[state.coreMessages.length - 1]) >= conversationLastMessageFingerprint.lastTime;
            }
          }
        }

        if (!isContinuous && state.detachedMessages.length) {
          // 确保不会产生二次游离数据集
          let isDetachedContinuous = false;
          if (conversationLastMessageFingerprint.type == TencentCloudChat.TYPES.CONV_GROUP) {
            isDetachedContinuous = getMessageSeq(state.detachedMessages[state.detachedMessages.length - 1]) >= conversationLastMessageFingerprint.lastSeq;
          } else {
            isDetachedContinuous = getMessageTime(state.detachedMessages[state.detachedMessages.length - 1]) >= conversationLastMessageFingerprint.lastTime;
          }
          if (!isDetachedContinuous) {
            state.detachedMessages = [];
            trace('游离缓存不连续，已清空', { conversationID, conversationLastMessageFingerprint, detachedLastMessage: state.detachedMessages[state.detachedMessages.length - 1] });
            inc('writes', 'detachedClear');
          }
        }

        const sortedIncoming = mergeMessagesWithAssign([], list);
        sortedIncoming.forEach((message) => {
          const isGroup = isGroupConversation(conversationID, message);
          if (isContinuous) {
            const appended = appendMessageWithRules(state.coreMessages, message, isGroup);
            if (appended && state.detachedMessages.length) {
              state.detachedMessages = [];
            }
          } else {
            appendMessageWithRules(state.detachedMessages, message, isGroup);
          }
        });

        if (state.coreMessages.length > MESSAGE_CACHE_LIMIT) {
          let trimmed = trimForLimit(state.coreMessages, MESSAGE_CACHE_LIMIT);
          state.coreMessages = trimmed.messages;
          if (trimmed.trimmedCount > 0) {
            state.coreBoundaryCompleted = false;
          }
        }
        if (state.detachedMessages.length > MESSAGE_CACHE_LIMIT) {
          let trimmed = trimForLimit(state.detachedMessages, MESSAGE_CACHE_LIMIT);
          state.detachedMessages = trimmed.messages;
          if (trimmed.trimmedCount > 0) {
            state.detachedBoundaryCompleted = false;
          }
        }

        inc('writes', 'messagePushUpdate');
        console.log('handleMessageReceived', state);
        await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
      });
    }
  };
  const handleMessageReadReceipt = async (messages) => {
    const userId = getCurrentUserId();
    if (!userId || !Array.isArray(messages) || !messages.length || !getCacheSwitch()) {
      return;
    }
    const grouped = new Map();
    messages.forEach((message) => {
      const conversationID = message?.conversationID;
      if (!conversationID) {
        return;
      }
      if (!grouped.has(conversationID)) {
        grouped.set(conversationID, []);
      }
      grouped.get(conversationID).push(message);
    });
    for (const [conversationID, list] of grouped.entries()) {
      await withMessageStateLock(userId, conversationID, async () => {
        const state = await readMessageState(userId, conversationID);
        list.forEach(message => {
          [state.detachedMessages, state.coreMessages].forEach(data => {
            data.forEach(e => {
              if (e.ID == message.messageID) {
                e.isPeerRead = true;
                e.readReceiptInfo.isPeerRead = true;
              }
            })
          })
        })
        trace('handleMessageReadReceipt');
        await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
      });
    }
  }
  // 标记消息的撤回
  const userRevokeMessage = async (users, conversationID) => {
    const userId = getCurrentUserId();
    if (!userId || !Array.isArray(users) || !users.length || !getCacheSwitch()) {
      return;
    }
    const grouped = new Map();
    users.forEach((user) => {
      if (!conversationID) {
        return;
      }
      if (!grouped.has(conversationID)) {
        grouped.set(conversationID, []);
      }
      grouped.get(conversationID).push(user);
    });
    for (const [conversationID, list] of grouped.entries()) {
      await withMessageStateLock(userId, conversationID, async () => {
        const state = await readMessageState(userId, conversationID);
        list.forEach(userID => {
          [state.detachedMessages, state.coreMessages].forEach(data => {
            data.filter(e => !e.isRevoked).forEach(e => {
              if (e.from == userID) {
                e.isRevoked = true;
              }
            });
          })
        })
        trace('userRevokeMessage');
        await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
      });
    }
  }
  const deleteMessage = async (messages) => {
    const userId = getCurrentUserId();
    if (!userId || !Array.isArray(messages) || !messages.length || !getCacheSwitch()) {
      return;
    }
    const grouped = new Map();
    messages.forEach((message) => {
      const conversationID = message?.conversationID;
      if (!conversationID) {
        return;
      }
      if (!grouped.has(conversationID)) {
        grouped.set(conversationID, []);
      }
      grouped.get(conversationID).push(message);
    });

    for (const [conversationID, list] of grouped.entries()) {
      await withMessageStateLock(userId, conversationID, async () => {
        const state = await readMessageState(userId, conversationID);
        list.forEach(item => {
          let findex = state.coreMessages.findIndex(e => e.ID == item.ID);
          if (findex > -1) {
            state.coreMessages.splice(findex, 1);
          }
        })
        trace('deleteMessage');
        await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
      });
    }
  }
  const clearHistoryMessage = async (conversationID) => {
    const userId = getCurrentUserId();
    return withMessageStateLock(userId, conversationID, async () => {
      const state = await readMessageState(userId, conversationID);
      [state.detachedMessages, state.coreMessages].forEach(data => {
        data.splice(0, data.length - 1);//清空
      })
      await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
    });
  }

  const getMessageList = async (options, otherOptions) => {
    inc('messageList', 'total');
    const chatClient = await getChatClient();
    const userId = getCurrentUserId();
    const conversationID = options?.conversationID;
    let requestCursor = options?.nextReqMessageID;
    const froce = otherOptions?.force;
    let role = otherOptions?.role;
    if (!conversationID || !userId || !getCacheSwitch()) {
      inc('messageList', 'bypass');
      inc('messageList', 'remote');
      trace('MISS messageList -> SDK.getMessageList (reason: bypass cache)', { conversationID, userId, hasConversationID: !!conversationID });
      return chatClient.getMessageList(options);
    }
    return withMessageStateLock(userId, conversationID, async () => {
      const state = await readMessageState(userId, conversationID);

      if (froce) {
        trace('force refresh, skip cache', { conversationID });
        state.coreMessages = [];
        state.coreBoundaryCompleted = false;
        state.detachedMessages = [];
        state.detachedBoundaryCompleted = false;
      }

      let isContinuous = false;
      const conversationLastMessageFingerprint = await resolveConversationLastMessageFingerprint(userId, conversationID);
      if (!state.coreMessages.length) isContinuous = false;
      else {
        let lastMsg = state.coreMessages[state.coreMessages.length - 1];
        if (conversationLastMessageFingerprint.type == TencentCloudChat.TYPES.CONV_GROUP) {
          isContinuous = getMessageSeq(lastMsg) >= conversationLastMessageFingerprint.lastSeq
            // 遇到一个真实数据，明明是同一条，但是seq相差1,time相同
            || (getMessageSeq(lastMsg) - conversationLastMessageFingerprint.lastSeq == -1
              && getMessageTime(lastMsg) >= conversationLastMessageFingerprint.lastTime);
        } else {
          isContinuous = getMessageTime(lastMsg) >= conversationLastMessageFingerprint.lastTime;
        }
        if (isContinuous) {
          trace('核心缓存连续', { conversationID, conversationLastMessageFingerprint, coreLastMessage: lastMsg });
        }
      }

      if (!isContinuous && state.detachedMessages.length) {
        // 确保不会产生二次游离数据集
        let isDetachedContinuous = false;
        let lastMsg = state.detachedMessages[state.detachedMessages.length - 1];
        if (conversationLastMessageFingerprint.type == TencentCloudChat.TYPES.CONV_GROUP) {
          isDetachedContinuous = getMessageSeq(lastMsg) >= conversationLastMessageFingerprint.lastSeq
            // 遇到一个真实数据，明明是同一条，但是seq相差1,time相同
            || (getMessageSeq(lastMsg) - conversationLastMessageFingerprint.lastSeq == -1
              && getMessageTime(lastMsg) >= conversationLastMessageFingerprint.lastTime);
        } else {
          isDetachedContinuous = getMessageTime(lastMsg) >= conversationLastMessageFingerprint.lastTime;
        }
        if (!isDetachedContinuous) {
          state.detachedMessages = [];
          trace('游离缓存不连续，已清空', { conversationID, conversationLastMessageFingerprint, detachedLastMessage: state.detachedMessages[state.detachedMessages.length - 1] });
          inc('writes', 'detachedClear');
        } else {
          trace('游离缓存连续', { conversationID, conversationLastMessageFingerprint, detachedLastMessage: lastMsg });
        }
      }


      // 不连续时，始终优先走 detached 通道；即便 detached 为空，也要触发一次远端最新页拉取来补洞。
      let targetKey = isContinuous ? 'coreMessages' : 'detachedMessages';

      let pageRes = getPageBeforeCursor(state[targetKey] || [], requestCursor, MESSAGE_PAGE_SIZE, role, userId);
      let page = pageRes.page;
      let isCompleted = pageRes.isCompleted && (targetKey === 'coreMessages' ? state.coreBoundaryCompleted : state.detachedBoundaryCompleted);
      let _nextIdx = -1;
      let latestMsg = null;
      if(pageRes.page.length) latestMsg =  pageRes.page[0];
      else if(state[targetKey].length) latestMsg = state[targetKey][0];
      do {
        const sourceMessages = state[targetKey] || [];
        let remoteTriggered = false;
        const needRemote = page.length < MESSAGE_PAGE_SIZE;
        if (needRemote && !isCompleted) {
          remoteTriggered = true;
          inc('messageList', 'remote');
          trace('MISS messageList -> SDK.getMessageList (reason: page insufficient)', {
            conversationID,
            target: targetKey,
            isCompleted,
            cacheCount: sourceMessages.length,
            pageCount: page.length,
            requestCursor: requestCursor || '',
            // fetchCursor: fetchCursor || '',
          });
          var getTask = Promise.resolve();
          var existMsg = sourceMessages.length ? sourceMessages[0] : null;
          if (existMsg || _nextIdx >= 0) {
            if (conversationLastMessageFingerprint.type == TencentCloudChat.TYPES.CONV_GROUP) {
              if (_nextIdx < 0) _nextIdx = getMessageSeq(existMsg) - 1;
              _nextIdx = Math.min(_nextIdx, getMessageSeq(existMsg) - 1);
              getTask = chatClient.getMessageListHopping({
                conversationID,
                sequence: _nextIdx,
                count: 15
              });
            } else {
              if (_nextIdx < 0) _nextIdx = getMessageTime(existMsg);
              _nextIdx = Math.min(_nextIdx, getMessageTime(existMsg));
              getTask = chatClient.getMessageListHopping({
                conversationID,
                time: _nextIdx,
                count: 15
              });
            }
          } else {
            getTask = chatClient.getMessageListHopping({
              conversationID,
              count: 15
            });
          }
          const remoteRes = await getTask;
          let remoteNextIdx = remoteRes?.data?.nextMessageSeq ?? remoteRes?.data?.nextMessageTime ?? null;
          
          isCompleted = remoteRes?.data?.isCompleted;
          if (isCompleted && conversationLastMessageFingerprint.type == TencentCloudChat.TYPES.CONV_GROUP) {
            if(_nextIdx >= 0 && (latestMsg.sequence - _nextIdx) < 500) {
              //500个“不可见消息”的容忍度，超过500个就认为游离数据集已经触底了核心数据集的边界
              isCompleted = false;
              remoteNextIdx = _nextIdx - 50;
            }
          }
          const noProgress = remoteNextIdx == null || remoteNextIdx === _nextIdx;
          _nextIdx = remoteNextIdx ?? _nextIdx;
          const remoteList = mergeMessagesWithAssign([], remoteRes?.data?.messageList || []);
          if (remoteList.length) {
            latestMsg = remoteList[0];
            if (targetKey === 'detachedMessages') {
              state.detachedMessages = mergeMessagesWithAssign(state.detachedMessages, remoteList);
              state.detachedBoundaryCompleted = !!isCompleted;
              const coreIdSet = new Set(state.coreMessages.map((item) => getMessageId(item)));
              const hasIntersection = state.detachedMessages.some((item) => coreIdSet.has(getMessageId(item)));
              if (hasIntersection) {
                // 游离数据集与核心数据集产生交集，可合并
                state.coreMessages = mergeMessagesWithAssign(state.coreMessages, state.detachedMessages);
                state.detachedMessages = [];
                if (state.coreMessages.length && getMessageId(state.coreMessages[0]) === remoteRes?.data?.nextReqMessageID) {
                  // 本次获取的数据集已经触底了核心数据集的边界，更新核心边界状态
                  state.coreBoundaryCompleted = isCompleted;
                }
                targetKey = "coreMessages";
                // } else if (state.detachedMessages.length >= state.coreMessages.length) {
                //   // 游离数据集长度超过核心数据集，且无交集，说明核心数据集过小了，可以直接替换核心数据集（相当于核心数据集过期了），并清空游离数据集
                //   state.coreMessages = state.detachedMessages;
                //   state.detachedMessages = [];
                //   state.coreBoundaryCompleted = !!isCompleted;
                //   targetKey = "coreMessages";
              }
            } else {
              state.coreMessages = mergeMessagesWithAssign(state.coreMessages, remoteList);
              state.coreBoundaryCompleted = !!isCompleted;
            }

            await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
          } else if (targetKey === 'coreMessages') {
            state.coreBoundaryCompleted = !!isCompleted;
            await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
          } else if (targetKey === 'detachedMessages') {
            state.detachedBoundaryCompleted = !!isCompleted;
          }

          if (!remoteList.length && noProgress) {
            if (targetKey === 'coreMessages') {
              state.coreBoundaryCompleted = true;
            } else {
              state.detachedBoundaryCompleted = true;
            }
            await writeMessageState(userId, conversationID, state, DEFAULT_DEBOUNCE_MS);
          }
          // }
        }

        if (!needRemote) {
          inc('messageList', 'hit');
          trace('HIT messageList', {
            conversationID,
            target: targetKey,
            pageCount: page.length,
            requestCursor: requestCursor || '',
          });
        } else if (!remoteTriggered) {
          inc('messageList', 'hit');
          inc('messageList', 'boundaryHit');
          trace('HIT messageList (boundary completed, no remote)', {
            conversationID,
            target: targetKey,
            pageCount: page.length,
            requestCursor: requestCursor || '',
          });
        } else {
          inc('messageList', 'miss');
        }

        const finalSource = state[targetKey] || [];
        let pageRes = getPageBeforeCursor(finalSource, requestCursor, MESSAGE_PAGE_SIZE, role, userId);
        page = pageRes.page;
        isCompleted = isCompleted && (targetKey === 'coreMessages' ? state.coreBoundaryCompleted : state.detachedBoundaryCompleted);
        // 如果是主数据集，读取主数据集的完成状态。如果是游离数据集，读取游离结束状态
        // page = filterMessage(page, role); 筛选已内置到 getPageBeforeCursor
        console.log('getMessageList-page2', page);
      } while (page.length < MESSAGE_PAGE_SIZE && !isCompleted);//page.length < MESSAGE_PAGE_SIZE && (!state.coreBoundaryCompleted)


      return composeMessageListResponse({
        list: page,
        isCompleted,

      });
    });
  };


  // const filterMessage = (data, role) => {
  //   const userId = getCurrentUserId();
  //   return data.filter(e => isVisibleMsg(e, role, userId));
  // }

  return {
    getConversationList,
    getMessageList,
    getUsersProfile,
    reqUserInfoInCache,
    saveUsersInfoToCache,
    refreshUserStatus,
    getCachedUserStatus,
    getBatchCachedUserStatus,
    isUserOnline,
    registerUserStatusScope,
    unregisterUserStatusScope,
    mergeUserStatusList,
    getGroupProfile,
    getGroupMemberList,
    writeMsgsExt,
    readMsgsExt,
    setMsgExt,
    getMsgExt,
    handleConversationListUpdate,
    deleteMessage,
    clearHistoryMessage,
    handleMessageReadReceipt,
    userRevokeMessage,
    handleRedPacketReceived,
    getRedPacketReceived,
    handleMessageReceived,
    onLoginSuccess,
    isDeletableMsg,
    getDebugStats: () => safeClone(stats),
    getFriendApplicationList,
    updateFriendApplicationList,
    printDebugStats: () => {
      const snapshot = safeClone(stats);
      console.log('[IM_CACHE_STATS]', snapshot);
      return snapshot;
    },
    resetDebugStats: () => {
      stats = createStats();
      return safeClone(stats);
    },
    setVerboseTrace: (enabled) => {
      verboseTrace = !!enabled;
    },
  };
};
